import { ReactElement, useMemo } from 'react'
import { GetServerSideProps } from 'next'
import { nocoSDK } from 'src/fixtures/modules/initialize'

import { DocumentShare, EuDocument, Visitor } from '@noco/http-client/lib/noco'
import { SWRCachePath } from 'src/fixtures/modules/swr-cach-path'
import { SWRConfig } from 'swr'
import { NextPageWithLayout } from 'src/pages/_app'
import { GlobalLayoutEu } from 'src/components/commons'
import { PageDocumentShareWithLink } from 'src/components/pages/PageDocumentShareWithLink'
import { getOrCreateVisitorFromToken } from 'src/fixtures/modules/visitor/functions'
import { deleteNoValue, getTokenFromCookie } from 'src/fixtures/utils'
import { ErrorRedirect, getEUViewErrorRedirect } from 'src/fixtures/utils/error'
import { MetaHeader } from 'src/components/organisms/MetaHeader'
import { useGetCotnactDocumentShare } from 'src/fixtures/modules/contactDocument/hooks'
import { useGetVisitor } from 'src/fixtures/modules/visitor/hooks'
import { getEuDocumentMetaTitle } from 'src/fixtures/modules/documents/utils'

type InitalProps = {}

type Props = {
  document: EuDocument // document は 100% 入ってくるはず
  documentId: string
  shareId: string
  documentShare: DocumentShare
  visitor: Visitor
  fallback: { [key in string]: any }
} & InitalProps

const Index: NextPageWithLayout<Props> = ({ documentId, shareId, fallback, document }: Props) => {
  const { data } = useGetCotnactDocumentShare({ documentShareId: shareId })
  const { data: visitor } = useGetVisitor()
  const organization = data?.organization
  const title = useMemo(
    () =>
      getEuDocumentMetaTitle({
        documentTitle: document.title,
        organizationName: organization?.name,
        hideAd: organization?.hideAd
      }),
    [document.title, organization?.hideAd, organization?.name]
  )
  return (
    <>
      <MetaHeader
        ogUrl={document.documentShare?.url}
        ogType="website"
        ogTitle={document.documentMiscSetting?.ogpTitle}
        ogDescription={document.documentMiscSetting?.ogpDescription}
        ogImage={document.documentMiscSetting?.ogpFile?.url}
        title={title}
        noIndex={!document.documentMiscSetting?.isPublicSearchEngine}
      />
      <SWRConfig value={{ fallback }}>
        <PageDocumentShareWithLink
          documentId={documentId}
          document={data?.document}
          visitor={visitor}
          organization={data?.organization}
          documentShare={data?.document?.documentShare}
          canRequestToExtendExpiredAt={data?.canRequestToExtendExpiredAt}
          documentShareId={shareId}
          hasMaterialContactable={data?.hasMaterialContactable}
          expiredOn={data?.expiredOn}
        />
      </SWRConfig>
    </>
  )
}

Index.getLayout = function getLayout(page: ReactElement) {
  return <GlobalLayoutEu>{page}</GlobalLayoutEu>
}

export default Index

export const getServerSideProps: GetServerSideProps = async ctx => {
  try {
    nocoSDK.initalize()
    const { shareId } = ctx.query as { shareId?: string }
    const documentApi = nocoSDK.client?.contactService.documentApi
    if (!shareId || !documentApi) return ErrorRedirect.NotFound

    const { contactToken } = getTokenFromCookie(ctx)
    const docRes = await documentApi
      ?.ApiFactory(contactToken)
      .contactV1DocumentsSharePublicLinkUlidGet({ publicLinkUlid: shareId })
      .then(res => res.data)
    const doc = docRes?.document
    if (!doc) return ErrorRedirect.NotFound

    // 公開されていない or 共有リンクになっていない => 404
    if (!doc.documentShare?.isActive) return ErrorRedirect.NotFound

    // visitor 情報のリクエスト。 visitor
    let visit = await getOrCreateVisitorFromToken(ctx, doc.organizationId!)
    if (!visit) return ErrorRedirect.NotFound

    // 共有リンクである場合には verify を叩く。-> document_contact が作られる
    if (doc.documentShare.isActive && visit.hasContact) {
      await documentApi?.ApiFactory(contactToken).contactV1DocumentsSharePublicLinkUlidVerifyMaterialContactablePut({
        publicLinkUlid: doc.documentShare.publicLinkUlid!
      })
    }

    // MEMO: @snuffy undefined 対策 (undeifined があると web 側にデータを流せないため)
    const documentShare = deleteNoValue(doc.documentShare)
    const visitor = deleteNoValue(visit)
    const document = deleteNoValue(doc)

    return {
      props: {
        document: document,
        documentId: doc.id!,
        shareId: shareId,
        documentShare,
        visitor,
        fallback: {
          [SWRCachePath.getVisitor()]: visitor,
          [SWRCachePath.getContactDocumentShare(shareId)]: deleteNoValue(docRes)
        }
      }
    }
  } catch (err: unknown) {
    return getEUViewErrorRedirect(err)
  }
}
